import { Gebruikersfout } from "../../utils/foutmelding";
import type { CdMap, TeUploaden } from "./cdHerkenning";

/** De map voor cd-zips; gelijk aan categoryToDossierSubfolder(BEELDVORMING_DVD) in de backend. */
export const BEELDVORMING_MAP = "Beeldvorming";

/** Een cd-map als zip (D1): STORE, Zip64, vaste volgorde en datum, dus voorspelbare bytes. */
export interface CdZip {
  /** "<mapnaam>.zip"; de server zet het dossierprefix ervoor. */
  naam: string;
  mapnaam: string;
  /** In zip-volgorde. */
  bestanden: TeUploaden[];
  /** Exacte grootte in bytes, vooraf berekend. */
  grootte: number;
}

const VIER_GIB = 0xffffffff;
const MAX_ITEMS_ZONDER_ZIP64 = 65_535;

export class CdTeVeelBestandenFout extends Gebruikersfout {
  constructor() {
    super(
      "Deze cd heeft meer dan 65.535 bestanden maar is kleiner dan 4 GB. Zo'n zip kan de webapp niet maken. Zip de cd-map op je computer en zet de zip in Beeldvorming.",
    );
    this.name = "CdTeVeelBestandenFout";
  }
}

function vergelijkBytes(a: Uint8Array, b: Uint8Array): number {
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) if (a[i] !== b[i]) return a[i] - b[i];
  return a.length - b.length;
}

/** Op de UTF-8-bytes van het relatieve pad, zoals de zip ze opslaat. */
export function zipVolgorde(bestanden: TeUploaden[]): TeUploaden[] {
  const enc = new TextEncoder();
  return bestanden
    .map((b) => ({ b, sleutel: enc.encode(b.relativePath) }))
    .sort((x, y) => vergelijkBytes(x.sleutel, y.sleutel))
    .map((x) => x.b);
}

/** De dag om 00:00 lokale tijd: de vaste tijdstempel van elk item. */
export function zipDatum(op: Date): Date {
  return new Date(op.getFullYear(), op.getMonth(), op.getDate());
}

/** Volgorde en exacte grootte; leest nog geen byte. client-zip pas laden als er een cd is. */
export async function planCdZip(
  cd: Pick<CdMap, "mapnaam" | "bestanden">,
): Promise<CdZip> {
  const { predictLength } = await import("client-zip");
  const bestanden = zipVolgorde(cd.bestanden);
  const grootte = Number(
    predictLength(
      bestanden.map((b) => ({ name: b.relativePath, size: b.file.size })),
    ),
  );
  // client-zip schrijft het Zip64-eindrecord pas boven 4 GiB.
  if (bestanden.length > MAX_ITEMS_ZONDER_ZIP64 && grootte < VIER_GIB) {
    throw new CdTeVeelBestandenFout();
  }
  return { naam: `${cd.mapnaam}.zip`, mapnaam: cd.mapnaam, bestanden, grootte };
}

/**
 * De zip als stroom, bestand voor bestand gelezen (nooit de hele cd in het
 * geheugen). `op` = aanmaakmoment van de batch: elke poging van dezelfde batch
 * levert dezelfde bytes, zodat een upload verder kan waar Graph stopte.
 */
export async function maakCdZipStroom(
  zip: CdZip,
  op: Date,
): Promise<ReadableStream<Uint8Array>> {
  const { makeZip } = await import("client-zip");
  const lastModified = zipDatum(op);
  async function* invoer() {
    for (const b of zip.bestanden) {
      yield {
        input: b.file,
        name: b.relativePath,
        lastModified,
        size: b.file.size,
      };
    }
  }
  return makeZip(invoer());
}
